import React, { useState, useEffect } from 'react';
import api from '../../utils/axiosConfig';
import { useNavigate } from 'react-router-dom';
import PageLoadingArea from '../../components/PageLoader/PageLoadingArea';
import usePageLoading from '../../hooks/usePageLoading';
import '../../styles/ListPage.css';

function ProjectList() {
    const navigate = useNavigate();
    const { isLoading, withLoading } = usePageLoading(true);
    const [projects, setProjects] = useState([]);
    const [selectedProjects, setSelectedProjects] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredProjects, setFilteredProjects] = useState([]);

    useEffect(() => {
        withLoading(async () => {
            const res = await api.get('/projects');
            setProjects(res.data);
            setFilteredProjects(res.data);
        }).catch((err) => console.error('Error fetching projects:', err));
    }, [withLoading]);

    useEffect(() => {
        const term = searchTerm.toLowerCase();
        const results = projects.filter(project =>
            (project.name || '').toLowerCase().includes(term) ||
            (project.clientName || '').toLowerCase().includes(term)
        );
        setFilteredProjects(results);
    }, [searchTerm, projects]);

    const handleCheckboxChange = (projectId) => {
        setSelectedProjects(prev =>
            prev.includes(projectId) ? prev.filter(id => id !== projectId) : [...prev, projectId]
        );
    };

    const handleSelectAll = (e) => {
        setSelectedProjects(e.target.checked ? filteredProjects.map(project => project.id) : []);
    };

    const handleDeleteSelected = () => {
        if (!window.confirm('Are you sure you want to delete the selected projects?')) return;
        Promise.all(selectedProjects.map(projectId => api.delete(`/projects/${projectId}`)))
            .then(() => {
                setProjects(prev => prev.filter(project => !selectedProjects.includes(project.id)));
                setSelectedProjects([]);
            })
            .catch(() => {
                alert('Error deleting projects');
            });
    };

    const allSelected = filteredProjects.length > 0 && selectedProjects.length === filteredProjects.length;

    return (
        <PageLoadingArea isLoading={isLoading}>
        <div className="list-page">
            <div className="list-page-header">
                <div className="list-page-title">
                    <h1>Projects</h1>
                    <p className="list-page-subtitle">
                        {filteredProjects.length} project{filteredProjects.length === 1 ? '' : 's'}
                    </p>
                </div>
                <div className="list-header-actions">
                    <div className="list-search-wrap">
                        <input
                            type="text"
                            placeholder="Search project or client"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="list-search-input"
                        />
                    </div>
                    <div className="list-button-group">
                        <button
                            type="button"
                            onClick={() => navigate('/admin')}
                            className="list-secondary-button"
                        >
                            Back to admin
                        </button>
                        <button type="button" onClick={() => navigate('/createProject')} className="list-add-button">
                            Add New Project
                        </button>
                        {selectedProjects.length > 0 && (
                            <button type="button" onClick={handleDeleteSelected} className="list-delete-button">
                                Delete ({selectedProjects.length})
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <div className="list-table-card">
                <div className="list-table-scroll">
                    <table className="list-table">
                        <thead>
                            <tr>
                                <th className="col-check">
                                    <input type="checkbox" checked={allSelected} onChange={handleSelectAll} aria-label="Select all" />
                                </th>
                                <th>Sr No</th>
                                <th>Project Name</th>
                                <th>Client</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredProjects.length === 0 ? (
                                <tr className="list-empty-row">
                                    <td colSpan="4">
                                        <div className="list-empty-state">
                                            <h3>No projects found</h3>
                                            <p>{searchTerm ? 'Try a different search term.' : 'Add your first project to get started.'}</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : filteredProjects.map((project, index) => (
                                <tr
                                    key={project.id}
                                    className={`list-row${selectedProjects.includes(project.id) ? ' is-selected' : ''}`}
                                    onClick={(e) => {
                                        if (e.target.type !== 'checkbox') navigate(`/createProject/${project.id}`);
                                    }}
                                >
                                    <td className="col-check" onClick={(e) => e.stopPropagation()}>
                                        <input
                                            type="checkbox"
                                            checked={selectedProjects.includes(project.id)}
                                            onChange={() => handleCheckboxChange(project.id)}
                                        />
                                    </td>
                                    <td>{index + 1}</td>
                                    <td><span className="list-primary-text">{project.name}</span></td>
                                    <td>{project.clientName}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
        </PageLoadingArea>
    );
}

export default ProjectList;
